import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEnvelope,
  faEnvelopeOpen,
  faTrash,
  faCheck,
  faExclamationTriangle,
  faPhone,
  faClock,
  faInbox
} from '@fortawesome/free-solid-svg-icons';
import { useDashboardAuth } from '../../context/DashboardAuthContext';
import api from '../../services/api';
import './ContactMessages.css';

const ContactMessages = () => {
  const { hasPermission, isSuperAdmin } = useDashboardAuth();
  const canManage = isSuperAdmin() || hasPermission('contact.manage');
  const [activeTab, setActiveTab] = useState('all');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [notification, setNotification] = useState(null);

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/dashboard-admin/contact-messages?status=${activeTab}`);
      setMessages(response.data || []);
    } catch (error) {
      console.error('Error fetching contact messages:', error);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleMarkRead = async (id) => {
    setProcessing(id);
    try {
      await api.put(`/dashboard-admin/contact-messages/${id}/read`);
      showNotification('success', 'Message marqué comme lu');
      fetchMessages();
    } catch (error) {
      showNotification('error', error.response?.data?.error || 'Erreur lors de la mise à jour');
    } finally {
      setProcessing(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce message ?')) return;
    setProcessing(id);
    try {
      await api.delete(`/dashboard-admin/contact-messages/${id}`);
      showNotification('success', 'Message supprimé');
      fetchMessages();
    } catch (error) {
      showNotification('error', error.response?.data?.error || 'Erreur lors de la suppression');
    } finally {
      setProcessing(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const tabs = [
    { id: 'all', label: 'Tous' },
    { id: 'unread', label: 'Non lus' },
    { id: 'read', label: 'Lus' }
  ];

  return (
    <div className="contact-messages-page">
      {notification && (
        <div className={`notification ${notification.type}`}>
          <FontAwesomeIcon icon={notification.type === 'success' ? faCheck : faExclamationTriangle} />
          {notification.message}
        </div>
      )}

      <div className="page-header">
        <div className="header-title">
          <h1><FontAwesomeIcon icon={faEnvelope} /> Messages de Contact</h1>
          <p>Messages envoyés depuis le formulaire de contact du site</p>
        </div>
      </div>

      <div className="tabs-bar">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="messages-container">
        {loading ? (
          <div className="loading">
            <div className="spinner"></div>
            <p>Chargement...</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="empty-state">
            <FontAwesomeIcon icon={faInbox} />
            <p>Aucun message {activeTab === 'unread' ? 'non lu' : activeTab === 'read' ? 'lu' : ''}</p>
          </div>
        ) : (
          <div className="messages-list">
            {messages.map(msg => (
              <div key={msg.id} className={`message-card ${!msg.is_read ? 'unread' : ''}`}>
                <div className="message-icon">
                  <FontAwesomeIcon icon={msg.is_read ? faEnvelopeOpen : faEnvelope} />
                </div>
                <div className="message-content">
                  <div className="message-header">
                    <h3>{msg.name}</h3>
                    <span className="message-date">
                      <FontAwesomeIcon icon={faClock} /> {formatDate(msg.created_at)}
                    </span>
                  </div>
                  <p className="message-contact-info">
                    <FontAwesomeIcon icon={faPhone} /> {msg.phone}
                    {msg.email && <> · <FontAwesomeIcon icon={faEnvelope} /> {msg.email}</>}
                  </p>
                  <p className="message-text">{msg.message}</p>
                </div>
                {canManage && (
                  <div className="message-actions">
                    {!msg.is_read && (
                      <button
                        className="btn-mark-read"
                        onClick={() => handleMarkRead(msg.id)}
                        disabled={processing === msg.id}
                      >
                        <FontAwesomeIcon icon={faCheck} /> Marquer lu
                      </button>
                    )}
                    <button
                      className="btn-delete"
                      onClick={() => handleDelete(msg.id)}
                      disabled={processing === msg.id}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactMessages;
